import { useTranslation } from 'react-i18next';

import { formatChartValue } from '@/features/charts/util/chart-styling-util';

export const BudgetLabel = ({
  viewBox,
  maxValue,
  unit,
  onMouseEnter,
  onMouseLeave,
  chartWidth,
  position = 'left',
  marginRight = 0,
  lineDirection = 'right',
}: {
  viewBox: any;
  maxValue: number;
  unit: string;
  onMouseEnter: (event: React.MouseEvent) => void;
  onMouseLeave: () => void;
  chartWidth?: number;
  position?: 'left' | 'right';
  marginRight?: number;
  lineDirection?: 'left' | 'right';
}) => {
  const formattedValue = formatChartValue(maxValue);
  const { t } = useTranslation('chart');

  const textContent = `${t('chart_stats.max_value_label')} ${formattedValue} ${unit}`;

  const textWidth = textContent.length * 6;
  const dynamicWidth = Math.max(70, textWidth);
  const chartEndX = viewBox.x + (viewBox.width || chartWidth || 1000);

  // Position bubble: if marginRight is set, position in the margin area (outside chart)
  // Otherwise position at the edge of the chart area
  const rectX =
    position === 'right'
      ? chartEndX + marginRight - dynamicWidth - 8 // In margin area, with padding from right edge
      : 0;
  const textX = rectX + dynamicWidth / 2;

  // Y position - at the budget line level
  const bubbleY = viewBox.y - 12;
  const textY = bubbleY + 16;

  // Calculate dotted line position based on lineDirection
  // 'left': Line goes from left edge to bubble (for Dashboard cards)
  // 'right': Line goes from bubble to right edge (for other charts)
  const lineStartX = lineDirection === 'left' ? 0 : rectX + dynamicWidth;
  const lineEndX = lineDirection === 'left' ? rectX - 8 : chartEndX;
  const lineY = viewBox.y;

  return (
    <>
      <g onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {/* Budget bubble */}
        <rect
          x={rectX}
          y={bubbleY}
          width={dynamicWidth}
          height={24}
          rx={12}
          ry={12}
          fill="hsl(var(--dark-container))"
          stroke="hsl(var(--custom-grey))"
          strokeWidth={0.5}
        />
        <text
          x={textX}
          y={textY}
          textAnchor="middle"
          fill="hsl(var(--custom-grey))"
          fontSize={9}
          fontWeight={500}
        >
          {textContent}
        </text>

        {/* Dotted line extending from bubble */}
        <line
          x1={lineStartX}
          y1={lineY}
          x2={lineEndX}
          y2={lineY}
          stroke="hsl(var(--custom-medium-grey))"
          strokeWidth={1}
          strokeDasharray="2 6"
          strokeLinecap="round"
        />
      </g>
    </>
  );
};
